import React from 'react';
import { SlidersHorizontal } from 'lucide-react';
import T from 'Components/Helpers/translate';
import { cn } from 'App/shadcn/lib/utils';
import { formatProspectingFilterValue } from './prospectingFilterValue';
import type { ProspectingFilterPanel } from './types';

/**
 * What the assistant did to the prospecting search, shown in the thread.
 *
 * The filters themselves live in the catalogue pane — this card is the record of
 * the turn: which filters this answer set, why each one, and which part of the
 * request became no filter at all.
 *
 * Mint rather than the beta purple it used to wear (WEB-9704): in the new design
 * the assistant's answer is one of the brand's tinted surfaces, and purple is
 * left to mean "unreleased" as it does everywhere else.
 */
const ProspectingFiltersCard = ({ panel }: { panel: ProspectingFilterPanel }) => {
	const { filters, unresolved } = panel;
	if (!filters.length && !unresolved?.length) return null;

	return (
		/* A mint surface rather than a bordered card with a beta stripe: in the new
		   design this is the assistant's answer, so it reads as one of the brand's
		   tinted surfaces inside the thread. #E7F9F9 is the Visuals guide's
		   `surface-base-mint`, which has no Tailwind token yet. */
		<div className="rounded-lg bg-[#e7f9f9] px-4 pb-4 pt-3.5">
			<div className="mb-0.5 flex items-center gap-2 border-b border-brand/10 pb-3">
				<SlidersHorizontal size={14} className="text-brand" />
				<span className="text-[12px] font-semibold tracking-[.01em] text-brand">
					{T('prospecting.v2.filtersCard.title', { amount: filters.length })}
				</span>
			</div>

			{filters.length > 0 ? (
				<ul className="flex flex-col">
					{/* Hairline between rows, none under the last: the card's own padding
					    closes it off. */}
					{filters.map((filter, index) => (
						<li
							key={filter.key}
							className={cn(
								'flex flex-col gap-px py-2.5',
								index < filters.length - 1 && 'border-b border-brand/[.08]'
							)}
						>
							<div className="text-[13px] text-grey-11">
								{filter.labelKey ? T(filter.labelKey) : filter.key}{' '}
								<span className="font-semibold text-brand tabular-nums">
									{formatProspectingFilterValue(filter.value)}
								</span>
							</div>
							{filter.reasoning ? (
								<div className="text-[12px] text-grey-10">{filter.reasoning}</div>
							) : null}
						</li>
					))}
				</ul>
			) : null}

			{unresolved?.length ? (
				<div className="mt-1 border-t border-brand/10 pt-2.5">
					<div className="text-[12px] text-grey-10">{T('prospecting.v2.filtersCard.unresolved')}</div>
					<div className="text-[13px] text-grey-11">{unresolved.join(' · ')}</div>
				</div>
			) : null}
		</div>
	);
};

export default ProspectingFiltersCard;
